<template>
  <RevButton
    data-qa="get-more-help"
    data-test="get-more-help"
    full-width="adaptive"
    :loading="isIntercomPending"
    :variant
    @click="openChat"
  >
    {{ i18n(translations.label) }}
  </RevButton>
</template>

<script lang="ts" setup>
import { navigateTo, useRouteLocationWithLocale } from '#imports'
import { computed, ref } from 'vue'

import { useAuthorizeIntercomUsers } from '@backmarket/nuxt-layer-care-platform/composables/intercom/useAuthorizeIntercomUsers'
import { useIntercom } from '@backmarket/nuxt-layer-care-platform/composables/intercom/useIntercom'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'

import { CUSTOMER_CONVERSATION } from '~/scopes/customer-conversation/route-names'

import translations from './GetMoreHelpAction.translations'
import type { GetMoreHelpActionProps } from './GetMoreHelpAction.types'

const { resolution, variant = 'secondary' } =
  defineProps<GetMoreHelpActionProps>()

const i18n = useI18n()
const injectLocale = useRouteLocationWithLocale()
const { callIntercomMethod, loadIntercomOnDemand } = useIntercom()
const { checkAuthorizedIntercomUsers } = useAuthorizeIntercomUsers()
const { openErrorToast } = useTheToast()
const isIntercomPending = ref<boolean>(false)

const conversationRoute = toNuxtLink(
  injectLocale({
    type: 'internal',
    name: CUSTOMER_CONVERSATION.RESOLUTION,
    params: {
      resolutionId: resolution.id,
    },
  }),
)

// TODO: TO CHANGE ONCE BACK END HAS DECIDED ON WHAT WE WILL USE TO DIFFERENTIATE CONVERSATION FROM INTERCOM
const isChatResolutionOptionInitiated = computed(
  () => resolution.resolutionOption.type === 'INTERCOM_ASYNC_CHAT',
)

async function openIntercomConversation() {
  if (resolution.externalId) {
    try {
      if (!checkAuthorizedIntercomUsers()) {
        openErrorToast({
          title: i18n(translations.toastErrorTitle),
          content: i18n(translations.toastErrorContent),
        })

        return
      }

      isIntercomPending.value = true
      await loadIntercomOnDemand()

      callIntercomMethod('showConversation', resolution.externalId)

      isIntercomPending.value = false
    } catch (e) {
      isIntercomPending.value = false
    }
  }
}

const openChat = () => {
  if (isChatResolutionOptionInitiated.value) {
    return openIntercomConversation()
  }

  return navigateTo(conversationRoute)
}
</script>
